import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogIn, LogOut, Coffee, Play } from "lucide-react";
import { api, errMsg, fmtTime, fmtDate, EVENT_LABEL } from "../../lib/api";
import { PageHeader, StatusPill } from "../../components/common";
import CameraCapture from "../../components/CameraCapture";

const ACTIONS = { clock_in: [LogIn, "dx-btn-gold"], break_start: [Coffee, "dx-btn-ghost"], break_end: [Play, "dx-btn-purple"], clock_out: [LogOut, "dx-btn-purple"] };

export const PhotoLog = ({ records, showName }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="photo-log">
    {records.map((r) => (
      <div key={r.id} className="dx-card overflow-hidden" data-testid={`photo-log-${r.id}`}>
        <img src={r.photo} alt={EVENT_LABEL[r.type]} className="w-full aspect-[4/3] object-cover bg-slate-100" />
        <div className="p-3 text-xs">
          <div className="font-semibold text-sm">{EVENT_LABEL[r.type]}</div>
          {showName && <div className="text-[#5B2A6E] font-medium">{r.staff_name}</div>}
          <div className="text-[#6b5a73]">{fmtDate(r.timestamp)} · {fmtTime(r.timestamp)}</div>
          {r.lat != null && <a className="text-[#5B2A6E] underline" target="_blank" rel="noreferrer" href={`https://maps.google.com/?q=${r.lat},${r.lng}`}>View location</a>}
        </div>
      </div>
    ))}
  </div>
);

export default function Attendance() {
  const [status, setStatus] = useState(null);
  const [records, setRecords] = useState([]);
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(new Date());

  const load = () => Promise.all([api.get("/attendance/status"), api.get("/attendance/records?days=7")]).then(([s, r]) => { setStatus(s.data); setRecords(r.data); });
  useEffect(() => { load(); const t = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(t); }, []);

  const confirm = async (photo, coords) => {
    setBusy(true);
    try {
      await api.post("/attendance", { type: action, photo, lat: coords?.lat, lng: coords?.lng });
      toast.success(`${EVENT_LABEL[action]} recorded`);
      setAction(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Time keeping" title="Clock in & out" subtitle="Every clock in, break and clock out needs a photo as evidence." />
      <div className="relative dx-grain overflow-hidden rounded-3xl bg-[#250E31] text-white p-8 sm:p-10">
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <div className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-bold">{clock.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</div>
            <div className="font-display text-5xl sm:text-6xl font-bold mt-2 tabular-nums" data-testid="attendance-clock">{clock.toLocaleTimeString("en-GB")}</div>
            <div className="mt-3 flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${status?.state === "working" ? "bg-emerald-400 dx-live" : status?.state === "on_break" ? "bg-[#F3C623]" : "bg-white/40"}`} />
              <StatusPill status={status?.state || "off"} testid="attendance-state" />
              {status?.last && status.state !== "off" && <span className="text-xs text-white/60">since {fmtTime(status.last.timestamp)}</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {(status?.allowed || []).map((a) => {
              const [Icon, cls] = ACTIONS[a];
              return <button key={a} className={`${cls} px-6 py-3.5 ${cls === "dx-btn-ghost" ? "text-white border-white/30 hover:bg-white/10" : ""}`} onClick={() => setAction(a)} data-testid={`attendance-${a.replace("_", "-")}-btn`}><Icon className="w-4 h-4" />{EVENT_LABEL[a]}</button>;
            })}
          </div>
        </div>
      </div>
      <h3 className="font-display font-semibold text-lg mt-10 mb-4">Last 7 days</h3>
      {records.length ? <PhotoLog records={records} /> : <p className="text-sm text-[#6b5a73]" data-testid="attendance-empty">No records yet. Clock in to start your day.</p>}
      <CameraCapture open={!!action} onOpenChange={(o) => !o && setAction(null)} label={EVENT_LABEL[action] || ""} onConfirm={confirm} busy={busy} />
    </div>
  );
}
